/** About You — the "Memso knows you" behavior profile.
 *
 *  Renders the synthesized facets Memso inferred from the user's own
 *  data (purchases, travel, dining, media, giving, rhythms). Every
 *  card frames its claim as inferred (dotted) with a Confirm / Not
 *  quite feedback loop, never as fact. "View underlying data" drills
 *  into the typed event rows behind the facet.
 *
 *  Brand voice: Memso, never "brain". No em dashes. */
import { useCallback, useEffect, useState } from "react";
import {
  UserCircle, Loader2, ChevronDown, Check, X, ShoppingBag, PieChart,
  Plane, Utensils, Users2, Clock, MessageSquare, Music, Activity,
  HeartHandshake, Home, RefreshCw,
} from "lucide-react";
import { api, type AboutYouFacet } from "../../lib/api";

const FACET_META: Record<string, { label: string; Icon: typeof UserCircle; tint: string }> = {
  brand_loyalty: { label: "Brand loyalty", Icon: ShoppingBag, tint: "amber" },
  spend_categories: { label: "Spend categories", Icon: PieChart, tint: "amber" },
  travel_patterns: { label: "Travel patterns", Icon: Plane, tint: "sky" },
  diet: { label: "Diet", Icon: Utensils, tint: "emerald" },
  family_composition: { label: "Family", Icon: Home, tint: "pink" },
  work_rhythm: { label: "Work rhythm", Icon: Clock, tint: "violet" },
  social_circle: { label: "Social circle", Icon: Users2, tint: "emerald" },
  time_rhythm: { label: "Daily rhythm", Icon: Clock, tint: "sky" },
  communication_style: { label: "Communication style", Icon: MessageSquare, tint: "violet" },
  media: { label: "Media taste", Icon: Music, tint: "pink" },
  fitness: { label: "Fitness", Icon: Activity, tint: "emerald" },
  charity: { label: "Giving", Icon: HeartHandshake, tint: "amber" },
};

function confColor(c: number): string {
  if (c >= 0.66) return "bg-emerald-500";
  if (c >= 0.33) return "bg-amber-500";
  return "bg-slate-400";
}

export function AboutYou() {
  const [facets, setFacets] = useState<AboutYouFacet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [drill, setDrill] = useState<Record<string, Record<string, unknown>[]>>({});
  const [verdicts, setVerdicts] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.aboutYou();
      setFacets(res.facets);
    } catch (e) {
      setError((e as Error).message || "Failed to load");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleDrill = useCallback(
    async (facet: string) => {
      setOpen((o) => ({ ...o, [facet]: !o[facet] }));
      if (!drill[facet]) {
        try {
          const r = await api.aboutYouFacet(facet);
          setDrill((d) => ({ ...d, [facet]: r.rows ?? [] }));
        } catch {
          setDrill((d) => ({ ...d, [facet]: [] }));
        }
      }
    },
    [drill],
  );

  const sendFeedback = useCallback(
    async (facet: string, verdict: "confirmed" | "rejected") => {
      setVerdicts((v) => ({ ...v, [facet]: verdict }));
      try {
        await api.aboutYouFeedback(facet, verdict);
      } catch {
        /* optimistic; ignore */
      }
    },
    [],
  );

  if (error) {
    return (
      <div className="rounded-2xl border border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-900/20 p-4 text-sm text-rose-700 dark:text-rose-300">
        {error}
      </div>
    );
  }
  if (!facets) {
    return (
      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading what Memso has learned about you...
      </div>
    );
  }

  const computed = facets.filter((f) => f.confidence > 0);

  return (
    <div className="space-y-3">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-violet-500" /> About you
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug max-w-prose">
            What Memso has quietly noticed from your own captures and threads, the way an
            advertiser would profile you, but it stays local to you. These are inferences,
            not facts. Tell Memso when it is right or wrong so it learns. Accuracy varies and
            improves as more data and connectors flow in.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </header>

      {computed.length === 0 && (
        <div className="rounded-2xl border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/15 p-4 text-sm text-amber-800 dark:text-amber-200">
          Memso is still learning you. As it mines your receipts, itineraries, and
          confirmations, facets will appear here with rising confidence.
        </div>
      )}

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {facets.map((f) => {
          const meta = FACET_META[f.facet] ?? { label: f.facet, Icon: UserCircle, tint: "slate" };
          const verdict = verdicts[f.facet] ?? f.status;
          const learning = f.confidence <= 0;
          return (
            <li key={f.facet}>
              <div
                className={`h-full rounded-2xl border p-4 transition-colors ${
                  verdict === "confirmed"
                    ? "border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-900/15"
                    : "border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10"
                }`}
              >
                <div className="flex items-center gap-2">
                  <meta.Icon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                    {meta.label}
                  </span>
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    {f.scope}
                  </span>
                </div>

                <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-snug">
                  {f.headline ?? "Memso is still learning this."}
                </p>

                {!learning && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                      <div
                        className={`h-full ${confColor(f.confidence)}`}
                        style={{ width: `${Math.round(f.confidence * 100)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">
                      {Math.round(f.confidence * 100)}% inferred
                    </span>
                  </div>
                )}

                {!learning && (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void sendFeedback(f.facet, "confirmed")}
                      disabled={verdict === "confirmed"}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 disabled:opacity-60"
                    >
                      <Check className="w-3 h-3" /> {verdict === "confirmed" ? "Confirmed" : "This is right"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void sendFeedback(f.facet, "rejected")}
                      disabled={verdict === "rejected"}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-60"
                    >
                      <X className="w-3 h-3" /> Not quite
                    </button>
                    <button
                      type="button"
                      onClick={() => void toggleDrill(f.facet)}
                      className="ml-auto inline-flex items-center gap-1 text-xs text-violet-600 dark:text-violet-400 hover:underline"
                    >
                      View underlying data
                      <ChevronDown
                        className={`w-3 h-3 transition-transform ${open[f.facet] ? "rotate-180" : ""}`}
                      />
                    </button>
                  </div>
                )}

                {open[f.facet] && (
                  <div className="mt-3 rounded-lg bg-white/70 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-2 max-h-56 overflow-auto">
                    {!drill[f.facet] ? (
                      <div className="text-xs text-slate-400 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                      </div>
                    ) : drill[f.facet].length === 0 ? (
                      <div className="text-xs text-slate-400">
                        No row-level events for this facet (it is derived from people and message
                        rhythms, not a typed event table).
                      </div>
                    ) : (
                      <table className="w-full text-[11px] text-slate-600 dark:text-slate-300">
                        <tbody>
                          {drill[f.facet].slice(0, 40).map((row, i) => (
                            <tr key={i} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                              <td className="py-1 align-top">
                                {Object.entries(row)
                                  .filter(
                                    ([k, v]) =>
                                      v != null &&
                                      v !== "" &&
                                      !["id", "user_id", "created_at", "source_packet_id", "captured_at"].includes(k),
                                  )
                                  .slice(0, 6)
                                  .map(([k, v]) => `${k}: ${String(v)}`)
                                  .join("  ·  ")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
